import { useCallback, useEffect, useRef } from 'react';

const SSE_BASE = 'https://realtime.espiaonfe.com.br/sse';
const MAX_ATTEMPTS = 3;

/**
 * Hook que gerencia conexões SSE (Server-Sent Events) por canal.
 *
 * Recursos:
 * - Uma conexão EventSource por canal, com dedupe de canais duplicados.
 * - Dedupe de mensagens por id e por assinatura de conteúdo (janela de 2s).
 * - Reconexão com backoff exponencial: até 3 tentativas (2s → 4s → 8s).
 * - Reconecta automaticamente ao evento `window.online`.
 *
 * @param {string|string[]|{company?:string,user?:string,groups?:string[],global?:string}} channels
 * @param {string} [sseBase]
 * @returns {{ on: (type: string, fn: Function) => Function }}
 */
export default function useSSEConnection(channels, sseBase = SSE_BASE) {
  const handlersRef    = useRef(new Map()); // type -> Set(fns)
  const connsRef       = useRef(new Map()); // canal -> EventSource
  const sigRef         = useRef('');        // assinatura estável dos canais ativos
  const seenIdsRef     = useRef(new Set()); // ids recentes (dedupe por id)
  const seenSigRef     = useRef(new Map()); // assinatura de conteúdo -> timestamp (dedupe 2s)
  const desiredChansRef = useRef([]);       // canais ainda ativos / desejados
  const reconnectRef   = useRef(new Map()); // chan -> { timer, attempt }

  // ─── Teardown ────────────────────────────────────────────────────────────

  const teardownAll = useCallback(() => {
    connsRef.current.forEach((es) => { try { es?.close(); } catch {} });
    connsRef.current.clear();
    reconnectRef.current.forEach((rec) => { try { clearTimeout(rec?.timer); } catch {} });
    reconnectRef.current.clear();
  }, []);

  useEffect(() => () => teardownAll(), [teardownAll]);

  // ─── API pública ─────────────────────────────────────────────────────────

  /** Registra handler para um tipo de evento. Retorna função de unbind. */
  const on = useCallback((type, fn) => {
    const m = handlersRef.current;
    if (!m.has(type)) m.set(type, new Set());
    m.get(type).add(fn);
    return () => m.get(type)?.delete(fn);
  }, []);

  // ─── Emit interno ────────────────────────────────────────────────────────

  const emit = useCallback((type, data) => {
    handlersRef.current.get(type)?.forEach((fn) => fn(data));
    handlersRef.current.get('*')?.forEach((fn) => fn({ type, data }));
  }, []);

  // ─── Conexão por canal ───────────────────────────────────────────────────

  const openChannel = useCallback((chan) => {
    try {
      if (connsRef.current.has(chan)) return;

      const es = new EventSource(`${sseBase}?chan=${encodeURIComponent(chan)}`);
      connsRef.current.set(chan, es);

      es.onopen = () => {
        const rec = reconnectRef.current.get(chan);
        if (rec?.timer) { try { clearTimeout(rec.timer); } catch {} }
        reconnectRef.current.set(chan, { attempt: 0, timer: null });
      };

      es.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }

        const actionKey  = String(msg.action || msg.payload?.action || '').toLowerCase();
        const typeKey    = String(msg.type || '').toLowerCase();

        // Rajadas de contagem e presença nunca são deduplicadas
        const isBurst    = ['atualizando', 'update', 'iniciando', 'iniciar', 'start', 'document', 'registrada', 'notify'].includes(actionKey);
        const isPresence = typeKey === 'presence';

        // Dedupe por id
        if (!isBurst && !isPresence) {
          const id = msg.id || msg.payload?.id || e.lastEventId;
          if (id) {
            if (seenIdsRef.current.has(id)) return;
            seenIdsRef.current.add(id);
            setTimeout(() => seenIdsRef.current.delete(id), 30000);
          }
        }

        // Dedupe por assinatura de conteúdo (2s)
        if (!isBurst && !isPresence) {
          const sig  = [msg.type, msg.action, msg.title, msg.message, msg.severity].join('|');
          const now  = Date.now();
          const last = seenSigRef.current.get(sig) || 0;
          if (now - last < 2000) return;
          seenSigRef.current.set(sig, now);
          setTimeout(() => {
            if (seenSigRef.current.get(sig) === now) seenSigRef.current.delete(sig);
          }, 2000);
        }

        const t = msg.type || 'message';
        emit(t, msg);
        // Encaminha para 'control' quando o evento tem action/target mas não é do tipo 'control'
        if (t !== 'control' && (msg.action || msg.target)) emit('control', msg);
      };

      es.onerror = () => {
        try { es.close(); } catch {}
        connsRef.current.delete(chan);

        if (!desiredChansRef.current.includes(chan)) return;

        const prev    = reconnectRef.current.get(chan) || { attempt: 0, timer: null };
        const attempt = (prev.attempt || 0) + 1;

        if (attempt > MAX_ATTEMPTS) {
          console.warn(`[SSE] Canal "${chan}" atingiu o limite de ${MAX_ATTEMPTS} tentativas. Reconexão suspensa.`);
          reconnectRef.current.delete(chan);
          return;
        }

        // Backoff: 2s → 4s → 8s (máx 30s)
        const delay = Math.min(30000, 2000 * Math.pow(2, attempt - 1));

        if (prev.timer) { try { clearTimeout(prev.timer); } catch {} }

        const timer = setTimeout(() => {
          if (desiredChansRef.current.includes(chan) && sigRef.current) {
            openChannel(chan);
          } else {
            reconnectRef.current.delete(chan);
          }
        }, delay);

        reconnectRef.current.set(chan, { attempt, timer });
      };
    } catch {
      // Ignora erros de criação; tenta reconectar no próximo ciclo do backoff
    }
  }, [emit, sseBase]);

  // ─── Efeito: abre/fecha conexões quando os canais mudam ─────────────────

  useEffect(() => {
    if (window.location.hostname === 'localhost') return; // sem realtime em dev

    const list = [];

    if (!channels) {
      teardownAll();
      sigRef.current = '';
      desiredChansRef.current = [];
      return;
    }

    if (typeof channels === 'string') {
      list.push(channels);
    } else if (Array.isArray(channels)) {
      list.push(...channels.filter(Boolean));
    } else if (typeof channels === 'object') {
      if (channels.company) list.push(channels.company);
      if (channels.user) list.push(channels.user);
      if (Array.isArray(channels.groups)) list.push(...channels.groups.filter(Boolean));
      if (channels.global) list.push(channels.global);
    }

    const uniq    = Array.from(new Set(list.filter(Boolean)));
    const ordered = uniq.slice().sort();
    const newSig  = JSON.stringify({ base: sseBase, chans: ordered });

    if (newSig === sigRef.current) return;

    sigRef.current = newSig;
    teardownAll();
    desiredChansRef.current = ordered;
    uniq.forEach((chan) => openChannel(chan));
  }, [channels, sseBase, teardownAll, openChannel]);

  // ─── Efeito: reconecta ao voltar online ─────────────────────────────────

  useEffect(() => {
    if (window.location.hostname === 'localhost') return;
    const onOnline = () => {
      desiredChansRef.current.forEach?.((chan) => {
        if (!connsRef.current.has(chan)) openChannel(chan);
      });
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [openChannel]);

  return { on };
}
